<template>
	<Dialog
		:dialog-visible="dialog"
		:title="isEditing ? 'Edit Tag' : 'Create a New Tag'"
		:agree-text="saving ? (isEditing ? 'Saving' : 'Creating') : isEditing ? 'Save' : 'Create'"
		cancel-text="Cancel"
		:disable-agree-button="saving"
		:agree-loading="saving"
		:options="props.zIndex !== undefined ? { width: '500px', zIndex: props.zIndex } : undefined"
		@cancel="cancel"
		@agree="agree">
		<template #dialog-body>
			<v-form ref="formRef" v-model="isFormValid">
				<!-- LSP-18472: Enter must submit, matching Create New Project. The listener has to be
				     on KEYUP, not keydown: this dialog renders through Dialog.vue, whose
				     @keydown.enter guard (LSP-16532) calls preventDefault() for any non-button
				     target so typing Enter in a field cannot submit or close the dialog. That
				     preventDefault also kills the <form>'s implicit submission, but it does not
				     suppress the following keyup — which is why CreateProjectDialog and
				     TagSelectionDialog both listen on @keyup.enter from inside the same Dialog. -->
				<v-text-field
					v-model.trim="tag.name"
					v-test="'create-new-tag-name'"
					:rules="nameRules"
					:counter="TAG_MAX_LENGTH"
					:counter-value="countCharacters"
					:autofocus="!tag.name"
					@keyup.enter="saving ? null : agree()">
					<template #label><FieldLabel label="Name (Required)" /></template>
				</v-text-field>
				<div class="d-flex align-center ga-3">
					<span class="text-body-2">Color</span>
					<LabelColorPicker v-model="tag.baseColor" :z-index="props.zIndex !== undefined ? props.zIndex + 1 : undefined" source="tag" />
				</div>
			</v-form>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
	import Dialog from "@/components/Dialog.vue";
	import LabelColorPicker from "@/components/tags/ColorPicker.vue";
	import type { Label } from "@/types/Label";
	import { ref } from "vue";
	import api from "@/api";
	import { TAG_MAX_LENGTH } from "@/api/constants";
	import { getRandomColor } from "@/helpers/tags";
	import { calculateTimeout, countCharacters, getMessageFromError } from "@/helpers/utilities";
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	const toast = useToast();

	// LSP-20845: callers hosted inside high-z overlays (the media lightbox's Tags panel sits at
	// z 1000010) must be able to stack this dialog above themselves; default keeps Dialog's own.
	const props = withDefaults(defineProps<{ zIndex?: number }>(), { zIndex: undefined });

	const dialog = ref(false);
	const saving = ref(false);
	const isEditing = ref(false);
	const editingId = ref<string | null>(null);
	const isFormValid = ref(false);
	const formRef = ref<any>(null);
	const resolve = ref<((value: Label | null) => void) | null>(null);
	const tag = ref<Partial<Label>>({ name: "", baseColor: getRandomColor() });

	const nameRules = [
		(v: string) => (v || "").trim().length > 0 || "Name is required.",
		(v: string) => countCharacters((v || "").trim()) <= TAG_MAX_LENGTH || `Please enter a shorter tag name.`
	];

	function resetForm() {
		formRef.value?.reset();
	}

	function open(defaultName = "", defaultColor?: string): Promise<Label | null> {
		resetForm();
		isEditing.value = false;
		editingId.value = null;
		saving.value = false;
		tag.value = { name: defaultName, baseColor: defaultColor || getRandomColor() };
		dialog.value = true;
		return new Promise((res) => {
			resolve.value = res;
		});
	}

	function editTag(label: Label): Promise<Label | null> {
		resetForm();
		isEditing.value = true;
		editingId.value = label.id;
		saving.value = false;
		tag.value = { name: label.name || "", baseColor: label.baseColor || getRandomColor() };
		dialog.value = true;
		return new Promise((res) => {
			resolve.value = res;
		});
	}

	async function agree() {
		// LSP-18472: `saving` has to latch SYNCHRONOUSLY, before the awaited validate() below.
		// It used to be set after that await, which left a window where a second submit had
		// already passed this point and created a duplicate tag. Clicking the footer button made
		// that hard to hit; Enter — especially held down, which repeats — makes it easy, so the
		// keyboard path is what turned it into a reachable defect. The template's
		// `saving ? null : agree()` guard alone cannot close it: it reads the same flag.
		if (saving.value) return;
		saving.value = true;
		try {
			const result = await formRef.value?.validate();
			if (!result?.valid) return;
			let record: Label;
			if (isEditing.value && editingId.value) {
				record = await api.updateLabel(editingId.value, { name: tag.value.name, baseColor: tag.value.baseColor });
				toast.success("All done! Your tag has been updated.");
			} else {
				record = await api.createLabel({
					name: tag.value.name,
					baseColor: tag.value.baseColor,
					failOnDuplicate: true
				});
				toast.success("All done! Your tag has been added.");
			}
			resolve.value?.(record);
			resetForm();
			dialog.value = false;
		} catch (e) {
			const message =
				getMessageFromError(e) || `Something went wrong ${isEditing.value ? "updating" : "creating"} the tag. Please try again.`;
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			saving.value = false;
		}
	}

	function cancel() {
		resetForm();
		dialog.value = false;
		resolve.value?.(null);
	}

	defineExpose({ open, editTag });
</script>
